// RITA-1525 (Teilpaket 2) — Zuordnung zwischen DOM-Auswahl und Modellpositionen.
//
// Die Seitenansicht rendert jeden Inline-Knoten als Element mit `data-knoten` (Textlauf: Span mit
// genau einem Textknoten, Platzhalter/Kästchen: Insel, Zeilenumbruch: <br>), Blöcke mit
// `data-block-id`, Listeneinträge mit `data-eintrag` und Bereiche mit `data-bereich`. Daraus lässt
// sich eine DOM-Stelle ohne Modellzugriff in eine Position umrechnen und umgekehrt. Innerhalb des
// Hosts steht jeder Textblock genau einmal; Kopf- und Fußzeile stehen auf jeder Seite, deshalb wird
// dort die aktive Seite gewählt. DOM-Stellen außerhalb eines Blocks (zwischen Seiten, an Inseln,
// nach Strg/Cmd+A) werden auf die nächste Blockposition abgebildet.
import { Auswahl, Bereich, Position, position, positionenGleich } from '../model/positionen';

/**
 * RITA-1533 (V10): Ein Platzhalter ist eine nicht editierbare Insel. Chromium legt den Auswahlfokus
 * beim Ziehen über eine Insel auf das Inselelement (Offset 0) oder in deren Textknoten und sperrt eine
 * Auswahl, deren Anker in einer Insel liegt, in diese Insel ein (gemessen 08.10.2026). Deshalb werden
 * Inselenden hier richtungsabhängig geklemmt – der Platzhalter gehört ganz zur Markierung –, und der
 * Editor führt eine auf einer Insel begonnene Mausauswahl selbst (`zielAmPunkt`, `inselGrenzen`).
 */

export interface DomStelle {
  node: Node;
  offset: number;
}

const BEREICHE: readonly string[] = ['text', 'kopf', 'fuss'];

const istElement = (node: Node): node is Element => node.nodeType === Node.ELEMENT_NODE;
const istLauf = (element: Element): boolean => element.classList.contains('vertrag-lauf');

/** Einheiten eines gerenderten Inline-Knotens (Textlauf: Zeichen, sonst 1). */
function elementEinheiten(element: Element): number {
  if (!element.hasAttribute('data-knoten')) return 0;
  return istLauf(element) ? (element.textContent ?? '').length : 1;
}

function einheitenVor(container: Element, anzahlKinder: number): number {
  return Array.from(container.children)
    .slice(0, anzahlKinder)
    .reduce((summe, kind) => summe + elementEinheiten(kind), 0);
}

function erstesTextkind(element: Element): Text | null {
  const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const erstes = walker.nextNode();
  return erstes ? (erstes as Text) : null;
}

function bereichVon(element: Element): Bereich | null {
  const bereichElement = element.closest('[data-bereich]');
  const bereich = bereichElement?.getAttribute('data-bereich') ?? '';
  return BEREICHE.includes(bereich) ? (bereich as Bereich) : null;
}

/** Anfang bzw. Ende eines Blockelements (Absatz oder Liste) als Position. */
function blockAnfang(block: Element): Position | null {
  const bereich = bereichVon(block);
  if (!bereich) return null;
  return position(bereich, block.getAttribute('data-block-id') ?? '', 0, 0);
}

function blockEnde(block: Element): Position | null {
  const bereich = bereichVon(block);
  if (!bereich) return null;
  const blockId = block.getAttribute('data-block-id') ?? '';
  if (block.tagName === 'UL') {
    const eintraege = Array.from(block.children).filter((kind) => kind.tagName === 'LI');
    const letzter = eintraege[eintraege.length - 1];
    if (!letzter) return null;
    return position(bereich, blockId, einheitenVor(letzter, letzter.children.length), eintraege.length - 1);
  }
  return position(bereich, blockId, einheitenVor(block, block.children.length), 0);
}

/** Rechnet eine DOM-Stelle innerhalb eines Blocks in eine Modellposition um; `null` außerhalb. */
export function domZuPosition(node: Node, offset: number): Position | null {
  const element = istElement(node) ? node : node.parentElement;
  if (!element) return null;
  const block = element.closest('[data-block-id]');
  if (!block || block.classList.contains('vertrag-seitenumbruch')) return null;
  const bereich = bereichVon(block);
  if (!bereich) return null;
  const blockId = block.getAttribute('data-block-id') ?? '';

  // Caret direkt auf der Liste: der Kindindex wählt den Eintrag.
  if (node === block && block.tagName === 'UL') {
    const eintraege = Array.from(block.children).filter((kind) => kind.tagName === 'LI');
    if (eintraege.length === 0) return null;
    if (offset >= eintraege.length) return blockEnde(block);
    return position(bereich, blockId, 0, offset);
  }

  let container: Element = block;
  let eintrag = 0;
  const eintragElement = element.closest('li[data-eintrag]');
  if (eintragElement && block.contains(eintragElement)) {
    container = eintragElement;
    eintrag = Number(eintragElement.getAttribute('data-eintrag'));
  }
  if (node === container) return position(bereich, blockId, einheitenVor(container, offset), eintrag);

  const knotenElement = element.closest('[data-knoten]');
  if (!knotenElement || knotenElement.parentElement !== container) {
    // Textknoten oder Element ohne Knotenzuordnung (z. B. abschließendes <br>): Ende des Inhalts.
    return position(bereich, blockId, einheitenVor(container, container.children.length), eintrag);
  }
  const basis = einheitenVor(container, Array.from(container.children).indexOf(knotenElement));
  if (node.nodeType === Node.TEXT_NODE) {
    if (!istLauf(knotenElement)) return position(bereich, blockId, basis + (offset > 0 ? 1 : 0), eintrag);
    let davor = 0;
    const walker = knotenElement.ownerDocument.createTreeWalker(knotenElement, NodeFilter.SHOW_TEXT);
    let aktuell = walker.nextNode();
    while (aktuell && aktuell !== node) {
      davor += (aktuell.textContent ?? '').length;
      aktuell = walker.nextNode();
    }
    return position(bereich, blockId, basis + davor + offset, eintrag);
  }
  // Elementstelle innerhalb des Knotens (Span, b/i/u oder die Insel selbst): 0 = davor, sonst danach.
  return position(bereich, blockId, basis + (offset === 0 ? 0 : elementEinheiten(knotenElement)), eintrag);
}

/**
 * Wie domZuPosition, bildet aber Stellen außerhalb eines Blocks (Seitenlücke, Seitenrand, Insel,
 * Host nach Strg/Cmd+A) auf den nächsten Block in Dokumentreihenfolge ab: den Anfang des nächsten
 * Blocks – oder, wenn das die bisherige Position wäre (Pfeil links), das Ende des vorherigen Blocks.
 * Nur Blöcke des gewünschten Bereichs zählen.
 */
export function domZuPositionGeklemmt(
  host: Element,
  node: Node,
  offset: number,
  bereich: Bereich,
  bisher: Position | null,
): Position | null {
  const direkt = domZuPosition(node, offset);
  if (direkt && direkt.bereich === bereich) return direkt;
  const dokument = host.ownerDocument;
  const range = dokument.createRange();
  try {
    range.setStart(node, offset);
  } catch {
    return null;
  }
  range.collapse(true);
  const bloecke = Array.from(host.querySelectorAll<HTMLElement>(`[data-bereich="${bereich}"] [data-block-id]`)).filter(
    (block) => !block.classList.contains('vertrag-seitenumbruch'),
  );
  if (bloecke.length === 0) return null;
  const grenze = (block: Element): number => {
    const vergleich = dokument.createRange();
    vergleich.selectNode(block);
    return range.compareBoundaryPoints(Range.START_TO_START, vergleich);
  };
  const naechsterIndex = bloecke.findIndex((block) => grenze(block) <= 0);
  const naechster = naechsterIndex >= 0 ? bloecke[naechsterIndex] : null;
  let vorheriger: Element | null = null;
  if (naechsterIndex > 0) vorheriger = bloecke[naechsterIndex - 1];
  else if (naechsterIndex < 0) vorheriger = bloecke[bloecke.length - 1];
  const vorwaerts = naechster ? blockAnfang(naechster) : null;
  const rueckwaerts = vorheriger ? blockEnde(vorheriger) : null;
  if (vorwaerts && bisher && positionenGleich(vorwaerts, bisher) && rueckwaerts) return rueckwaerts;
  return vorwaerts ?? rueckwaerts;
}

/** Sucht den Container (Block bzw. Listeneintrag) einer Position innerhalb des Hosts. */
function containerZu(host: Element, stelle: Position, aktiveSeite: number): Element | null {
  const bereich =
    stelle.bereich === 'text'
      ? '[data-bereich="text"]'
      : `[data-seite="${aktiveSeite}"] [data-bereich="${stelle.bereich}"]`;
  const block = host.querySelector(`${bereich} [data-block-id="${stelle.blockId}"]`);
  if (!block) return null;
  if (block.tagName !== 'UL') return block;
  return block.querySelector(`li[data-eintrag="${stelle.eintrag}"]`);
}

/**
 * Rechnet eine Modellposition in eine DOM-Stelle um. An einer Grenze zwischen zwei Knoten steht
 * der Caret am Ende des vorherigen Textlaufs; vor/hinter einer Insel auf dem Container.
 */
export function positionZuDom(host: Element, stelle: Position, aktiveSeite: number): DomStelle | null {
  const container = containerZu(host, stelle, aktiveSeite);
  if (!container) return null;
  const kinder = Array.from(container.children);
  let anfang = 0;
  for (let index = 0; index < kinder.length; index += 1) {
    const kind = kinder[index];
    if (kind.hasAttribute('data-knoten')) {
      const laenge = elementEinheiten(kind);
      const ende = anfang + laenge;
      const lauf = istLauf(kind);
      if (stelle.offset < ende || (stelle.offset === ende && lauf)) {
        if (lauf) {
          const textknoten = erstesTextkind(kind);
          return textknoten ? { node: textknoten, offset: stelle.offset - anfang } : { node: kind, offset: 0 };
        }
        return { node: container, offset: stelle.offset <= anfang ? index : index + 1 };
      }
      anfang = ende;
    }
  }
  const abschluss = kinder.findIndex((kind) => kind.hasAttribute('data-abschluss'));
  return { node: container, offset: abschluss >= 0 ? abschluss : kinder.length };
}

/** Die Platzhalter-Insel, in der eine DOM-Stelle liegt (Element oder Textknoten); Kästchen zählen nicht. */
export function platzhalterInsel(host: Element, node: Node | null): Element | null {
  if (!node) return null;
  const element = istElement(node) ? node : node.parentElement;
  const insel = element?.closest('[data-knoten][contenteditable="false"][data-platzhalter]');
  return insel && host.contains(insel) ? insel : null;
}

/** Positionen direkt vor und hinter einer Insel. */
export function inselGrenzen(insel: Element): { davor: Position; dahinter: Position } | null {
  const davor = domZuPosition(insel, 0);
  const dahinter = domZuPosition(insel, 1);
  return davor && dahinter ? { davor, dahinter } : null;
}

/** Vergleicht zwei DOM-Stellen in Dokumentreihenfolge (<0: a vor b). */
function domStellenVergleich(a: DomStelle, b: DomStelle): number {
  const dokument = a.node.ownerDocument ?? document;
  const ra = dokument.createRange();
  const rb = dokument.createRange();
  try {
    ra.setStart(a.node, a.offset);
    rb.setStart(b.node, b.offset);
  } catch {
    return 0;
  }
  ra.collapse(true);
  rb.collapse(true);
  return ra.compareBoundaryPoints(Range.START_TO_START, rb);
}

/**
 * Richtungsabhängiges Klemmen eines Auswahlendes in einer Platzhalter-Insel (V10b): Das spätere Ende
 * einer Auswahl liegt hinter der Insel, das frühere davor – so zählt ein Platzhalter, auf dem die
 * Markierung beginnt oder endet, immer ganz mit. `null`, wenn das Ende in keiner Insel liegt.
 */
function inselEndeGeklemmt(host: Element, ende: DomStelle, anderesEnde: DomStelle): Position | null {
  const insel = platzhalterInsel(host, ende.node);
  if (!insel) return null;
  const grenzen = inselGrenzen(insel);
  if (!grenzen) return null;
  // Das andere Ende in derselben Insel: Richtung über die Offsets; sonst über die Dokumentreihenfolge.
  const andereInsel = platzhalterInsel(host, anderesEnde.node);
  if (andereInsel === insel) return null;
  return domStellenVergleich(ende, anderesEnde) > 0 ? grenzen.dahinter : grenzen.davor;
}

/**
 * Ziel einer selbst geführten Mausauswahl am Bildschirmpunkt (RITA-1533, V10). `null`, wenn der Punkt
 * außerhalb des Hosts liegt (Leiste, Werkzeugleiste, App-Kopf) oder der Browser keine Stelle kennt
 * (Seitenlücke) – dann bleibt die zuletzt gültige Markierung. Auf einer Platzhalter-Insel liefert das
 * Ergebnis beide Grenzen, damit der Aufrufer je nach Richtung wählt.
 */
export function zielAmPunkt(
  host: Element,
  x: number,
  y: number,
  bereich: Bereich,
  bisher: Position | null,
): { position: Position; insel: { davor: Position; dahinter: Position } | null } | null {
  const dokument = host.ownerDocument;
  const mitCaret = dokument as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  let stelle: DomStelle | null = null;
  if (typeof mitCaret.caretRangeFromPoint === 'function') {
    const range = mitCaret.caretRangeFromPoint(x, y);
    if (range) stelle = { node: range.startContainer, offset: range.startOffset };
  } else if (typeof mitCaret.caretPositionFromPoint === 'function') {
    const treffer = mitCaret.caretPositionFromPoint(x, y);
    if (treffer) stelle = { node: treffer.offsetNode, offset: treffer.offset };
  }
  if (!stelle || !host.contains(stelle.node)) return null;
  const insel = platzhalterInsel(host, stelle.node);
  if (insel) {
    const grenzen = inselGrenzen(insel);
    if (!grenzen || grenzen.davor.bereich !== bereich) return null;
    return { position: grenzen.dahinter, insel: grenzen };
  }
  const direkt = domZuPosition(stelle.node, stelle.offset);
  if (direkt && direkt.bereich === bereich) return { position: direkt, insel: null };
  const geklemmt = domZuPositionGeklemmt(host, stelle.node, stelle.offset, bereich, bisher);
  return geklemmt ? { position: geklemmt, insel: null } : null;
}

/** Liest die DOM-Auswahl streng (beide Enden in Blöcken desselben Bereichs); sonst `null`. */
export function auswahlAusDom(host: Element): Auswahl | null {
  const auswahl = host.ownerDocument.getSelection();
  if (!auswahl || auswahl.rangeCount === 0 || !auswahl.anchorNode || !auswahl.focusNode) return null;
  if (!host.contains(auswahl.anchorNode) || !host.contains(auswahl.focusNode)) return null;
  const anker = domZuPosition(auswahl.anchorNode, auswahl.anchorOffset);
  const fokus = domZuPosition(auswahl.focusNode, auswahl.focusOffset);
  if (!anker || !fokus || anker.bereich !== fokus.bereich) return null;
  return { anker, fokus };
}

export interface GelesenerDomStand {
  auswahl: Auswahl;
  /** Die DOM-Auswahl lag (teilweise) außerhalb eines Blocks oder in einer Insel und sollte neu gesetzt werden. */
  korrigieren: boolean;
}

/**
 * Liest die DOM-Auswahl für den aktiven Bereich und klemmt Stellen außerhalb eines Blocks. Meldet,
 * wenn die DOM-Auswahl an einer Stelle liegt, an der Chromium keine Eingabe annimmt (Insel, Lücke).
 */
export function auswahlAusDomGeklemmt(
  host: Element,
  bereich: Bereich,
  bisher: Auswahl | null,
): GelesenerDomStand | null {
  const auswahl = host.ownerDocument.getSelection();
  if (!auswahl || auswahl.rangeCount === 0 || !auswahl.anchorNode || !auswahl.focusNode) return null;
  if (!host.contains(auswahl.anchorNode) || !host.contains(auswahl.focusNode)) return null;
  const inInsel = (node: Node): boolean => {
    const element = istElement(node) ? node : node.parentElement;
    const insel = element?.closest('[contenteditable="false"]');
    return !!insel && host.contains(insel);
  };
  const ankerDirekt = domZuPosition(auswahl.anchorNode, auswahl.anchorOffset);
  const fokusDirekt = domZuPosition(auswahl.focusNode, auswahl.focusOffset);
  const korrigieren =
    !ankerDirekt ||
    !fokusDirekt ||
    ankerDirekt.bereich !== bereich ||
    fokusDirekt.bereich !== bereich ||
    inInsel(auswahl.anchorNode) ||
    inInsel(auswahl.focusNode);
  // V10b: Ein nicht kollabiertes Auswahlende auf einem Platzhalter nimmt ihn ganz mit (richtungsabhängig).
  const ankerStelle: DomStelle = { node: auswahl.anchorNode, offset: auswahl.anchorOffset };
  const fokusStelle: DomStelle = { node: auswahl.focusNode, offset: auswahl.focusOffset };
  const ankerInsel = auswahl.isCollapsed ? null : inselEndeGeklemmt(host, ankerStelle, fokusStelle);
  const fokusInsel = auswahl.isCollapsed ? null : inselEndeGeklemmt(host, fokusStelle, ankerStelle);
  const anker =
    ankerInsel ?? domZuPositionGeklemmt(host, auswahl.anchorNode, auswahl.anchorOffset, bereich, bisher?.anker ?? null);
  const fokus =
    fokusInsel ?? domZuPositionGeklemmt(host, auswahl.focusNode, auswahl.focusOffset, bereich, bisher?.fokus ?? null);
  if (!anker || !fokus) return null;
  if ((ankerInsel && anker.bereich !== bereich) || (fokusInsel && fokus.bereich !== bereich)) return null;
  return { auswahl: { anker, fokus }, korrigieren };
}

/** Setzt die DOM-Auswahl auf die Modellauswahl (nur wenn sie abweicht). Liefert false, wenn das Ziel fehlt. */
export function auswahlInDom(host: Element, auswahl: Auswahl, aktiveSeite: number): boolean {
  const anker = positionZuDom(host, auswahl.anker, aktiveSeite);
  const fokus = positionZuDom(host, auswahl.fokus, aktiveSeite);
  if (!anker || !fokus) return false;
  const domAuswahl = host.ownerDocument.getSelection();
  if (!domAuswahl) return false;
  if (
    domAuswahl.anchorNode === anker.node &&
    domAuswahl.anchorOffset === anker.offset &&
    domAuswahl.focusNode === fokus.node &&
    domAuswahl.focusOffset === fokus.offset
  ) {
    return true;
  }
  if (typeof domAuswahl.setBaseAndExtent === 'function') {
    domAuswahl.setBaseAndExtent(anker.node, anker.offset, fokus.node, fokus.offset);
  } else {
    const range = host.ownerDocument.createRange();
    range.setStart(anker.node, anker.offset);
    range.setEnd(fokus.node, fokus.offset);
    domAuswahl.removeAllRanges();
    domAuswahl.addRange(range);
  }
  return true;
}

/** Rechnet eine DOM-Range (z. B. aus getTargetRanges) in eine Modellauswahl um. */
export function rangeZuAuswahl(range: {
  startContainer: Node;
  startOffset: number;
  endContainer: Node;
  endOffset: number;
}): Auswahl | null {
  const anker = domZuPosition(range.startContainer, range.startOffset);
  const fokus = domZuPosition(range.endContainer, range.endOffset);
  if (!anker || !fokus || anker.bereich !== fokus.bereich) return null;
  return { anker, fokus };
}

/** Position am Bildschirmpunkt (Klick), soweit der Browser das kann; auch in Inseln. */
export function positionAmPunkt(dokument: Document, x: number, y: number): Position | null {
  const mitCaret = dokument as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof mitCaret.caretRangeFromPoint === 'function') {
    const range = mitCaret.caretRangeFromPoint(x, y);
    return range ? domZuPosition(range.startContainer, range.startOffset) : null;
  }
  if (typeof mitCaret.caretPositionFromPoint === 'function') {
    const stelle = mitCaret.caretPositionFromPoint(x, y);
    return stelle ? domZuPosition(stelle.offsetNode, stelle.offset) : null;
  }
  return null;
}
